import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, RefreshCw } from "lucide-react";
import { api, formatDate } from "../api";
import { useApi } from "../hooks";
import { useAuth } from "../auth";
import Dialog from "../components/Dialog";
import FormBuilder from "../components/crm/FormBuilder";
import type { CrmForm } from "../components/crm/types";
import { EmptyState, ErrorState, PageHeader, PageSkeleton } from "../components/Ui";

/** Lead forms: the list, and (with ?form=<id>) one form's builder. */
export default function CrmForms() {
  const [params, setParams] = useSearchParams();
  const formId = Number(params.get("form")) || null;
  const openForm = (id: number | null) => setParams((cur) => {
    const next = new URLSearchParams(cur);
    if (id) next.set("form", String(id)); else { next.delete("form"); next.delete("tab"); }
    next.delete("contact");
    return next;
  });
  if (formId) return <FormBuilder key={formId} id={formId} onBack={() => openForm(null)} />;
  return <FormList onOpen={openForm} />;
}

/** GET /api/crm/form-stats: each form's last 30 days (utils/crmFormStats.js). */
type FormNumbers = { forms: Record<string, { started: number; finished: number; completion: number | null }> };

function FormList({ onOpen }: { onOpen: (id: number) => void }) {
  const list = useApi<CrmForm[]>("/api/crm/forms");
  // Completion is extra: the list shows without it, and "—" until it arrives.
  const numbers = useApi<FormNumbers>("/api/crm/form-stats?days=30");
  const completion = (id: number) => {
    const c = numbers.data?.forms[String(id)]?.completion;
    return c === null || c === undefined ? null : `${Math.round(c * 100)}%`;
  };
  const { user } = useAuth();
  const canWrite = user?.role === "superadmin" || user?.role === "exec";
  const [creating, setCreating] = useState(false);
  const newForm = canWrite && <button className="button" onClick={() => setCreating(true)}><Plus aria-hidden="true" />New form</button>;
  const rows = list.data || [];
  const refreshing = list.refreshing || numbers.refreshing;
  return (
    <>
      {/* Refresh is the header button every list page has, not an icon in the table's footer. */}
      <PageHeader title="Forms" actions={<div className="header-controls">
        <button className="button secondary" disabled={refreshing} onClick={() => { list.reload(); numbers.reload(); }}><RefreshCw className={refreshing ? "spin" : ""} aria-hidden="true" />{refreshing ? "Refreshing…" : "Refresh"}</button>
        {newForm}
      </div>} />
      {list.loading && !list.data ? <PageSkeleton cards={0} />
        : list.error ? <ErrorState message={list.error} onRetry={list.reload} />
        : !rows.length ? <section className="panel"><EmptyState title="No forms yet" description="A form asks a few qualifying questions, adds the lead to your pipeline and can book the call right away." action={newForm || undefined} /></section>
        : (
          <section className="panel table-wrap">
            <table className="module-table crm-table crm-forms-table">
              <thead><tr><th>Form</th><th>Status</th><th className="num">Leads</th><th className="num">Qualified</th><th className="num">Last 30 days</th><th className="num" title="Finished out of started, last 30 days">Completion</th><th>Last lead</th><th>Updated</th></tr></thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id} className="is-clickable" onClick={() => onOpen(f.id)}>
                    <td data-label="Form"><button type="button" className="crm-row-open" onClick={(e) => { e.stopPropagation(); onOpen(f.id); }}>{f.name}</button><small className="crm-muted crm-block">/f/{f.slug}</small></td>
                    <td data-label="Status"><span className={`crm-status ${f.status}`}>{f.status === "live" ? "Live" : "Draft"}</span></td>
                    <td data-label="Leads" className="num">{f.submissions || 0}</td>
                    <td data-label="Qualified" className="num">{f.qualified || 0}</td>
                    <td data-label="Last 30 days" className="num">{f.last_30_days || 0}</td>
                    <td data-label="Completion" className="num" data-empty={completion(f.id) ? undefined : ""}>{completion(f.id) || <span className="crm-muted">—</span>}</td>
                    <td data-label="Last lead" data-empty={f.last_submission_at ? undefined : ""}>{formatDate(f.last_submission_at || null)}</td>
                    <td data-label="Updated">{formatDate(f.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="crm-table-foot">
              <span>{rows.length} {rows.length === 1 ? "form" : "forms"}</span>
            </div>
          </section>
        )}
      {creating && <NewFormDialog onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); onOpen(id); }} />}
    </>
  );
}

function NewFormDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function create(template: "casino" | "blank") {
    setBusy(true); setError("");
    try {
      const f = await api<CrmForm>("/api/crm/forms", { method: "POST", body: JSON.stringify({ template }) });
      onCreated(f.id);
    } catch (e) { setError(e instanceof Error ? e.message : "The form could not be created."); setBusy(false); }
  }
  return (
    <Dialog title="New form" onClose={onClose}>
      <div className="crm-templates">
        <button type="button" className="crm-template" disabled={busy} onClick={() => void create("casino")}>
          <strong>Casino partner survey</strong>
          <span>Hotel on property, monthly gaming revenue, in-house team, events, then contact details. No hotel or under $4M is not a fit.</span>
        </button>
        <button type="button" className="crm-template" disabled={busy} onClick={() => void create("blank")}>
          <strong>Blank form</strong>
          <span>One question and the contact details screen.</span>
        </button>
      </div>
      {error && <p className="crm-error" role="alert">{error}</p>}
    </Dialog>
  );
}
